import React, { useEffect } from "react";
import { WidgetWrapper } from "../../../../components/WidgetWrapper/WidgetWrapper";
import { EditField } from "../../../../components/EditPanel/types";
import { TrendingCardWidgetProps, TrendingCardWidgetState } from "./types";
import { useWidgetEditable } from "../../../../hooks/useWidgetEditable";
import { FONT_WEIGHT_OPTIONS_KEYWORDS } from "../../../../utils/fontWeightOptions";
import { themeColor } from "../../../../types/color";

/**
 * Load a Google Font stylesheet into document.head.
 * This is necessary because fonts loaded inside Shadow DOM don't register
 * @font-face declarations globally, causing the font to not render.
 */
const loadGoogleFontInHead = (fontUrl: string): void => {
  // Check if already loaded
  const existingLink = document.querySelector(`link[href="${fontUrl}"]`);
  if (existingLink) return;

  // Inject into document.head (works with Shadow DOM)
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = fontUrl;
  document.head.appendChild(link);
};

const defaultState: TrendingCardWidgetState = {
  // Carousel gap
  carouselGap: 16,

  // Card container
  cardBorderRadius: 10,

  // "Explore now" pill
  pillBgColor: themeColor("secondary"),
  pillBorderColor: themeColor("secondary"),
  pillTextColor: themeColor("textPrimary"),
  pillPaddingX: 12,
  pillPaddingY: 6,
  pillBorderRadius: 50,

  // Arrow button
  arrowBgColor: themeColor("secondary"),
  arrowBorderColor: themeColor("secondary"),
  arrowIconColor: themeColor("textPrimary"),

  // Title
  titleColor: themeColor("textPrimary"),
  titleFontWeight: "semibold",
  titleFontFamily: "inherit",
};

const editFields: EditField[] = [
  // Carousel gap
  {
    key: "carouselGap",
    label: "Gap Between Cards",
    type: "number",
    min: 8,
    max: 32,
    step: 4,
  },

  // Card container
  {
    key: "cardBorderRadius",
    label: "Card Border Radius",
    type: "number",
    min: 0,
    max: 24,
    step: 2,
  },

  // "Explore now" pill
  { key: "pillBgColor", label: "Pill Background", type: "color" },
  { key: "pillBorderColor", label: "Pill Border Color", type: "color" },
  { key: "pillTextColor", label: "Pill Text Color", type: "color" },
  {
    key: "pillPaddingX",
    label: "Pill Padding X",
    type: "number",
    min: 8,
    max: 20,
    step: 2,
  },
  {
    key: "pillPaddingY",
    label: "Pill Padding Y",
    type: "number",
    min: 4,
    max: 12,
    step: 2,
  },
  {
    key: "pillBorderRadius",
    label: "Pill Border Radius",
    type: "number",
    min: 4,
    max: 50,
    step: 2,
  },

  // Arrow button
  { key: "arrowBgColor", label: "Arrow Background", type: "color" },
  { key: "arrowBorderColor", label: "Arrow Border Color", type: "color" },
  { key: "arrowIconColor", label: "Arrow Icon Color", type: "color" },

  // Title
  { key: "titleColor", label: "Title Color", type: "color" },
  {
    key: "titleFontWeight",
    label: "Title Font Weight",
    type: "options",
    options: [...FONT_WEIGHT_OPTIONS_KEYWORDS],
  },
  {
    key: "titleFontFamily",
    label: "Title Font Family",
    type: "font",
  },
];

export const TrendingCardWidget: React.FC<TrendingCardWidgetProps> = ({
  initialState = {},
  widgetId,
  displayName,
  children,
}) => {
  const {
    state,
    isLoading,
    isSaving,
    handleEditChange,
    handleSave,
    themeColors,
    themeFonts,
    resolveColor,
    canSave,
    getEditValues,
  } = useWidgetEditable<TrendingCardWidgetState>(
    widgetId,
    { ...defaultState, ...initialState },
    editFields,
  );

  // Resolve font family - if "inherit", use inherit; otherwise look up from themeFonts
  const fontKey = state.titleFontFamily;
  const fontData =
    fontKey && fontKey !== "inherit" ? themeFonts[fontKey] : null;

  // For Google Fonts, we need to load via <link> tag in document.head
  const googleFontLink =
    fontData && fontData.format === "google" ? fontData.url : null;

  // Load Google Font into document.head when font changes
  useEffect(() => {
    if (googleFontLink) {
      loadGoogleFontInHead(googleFontLink);
    }
  }, [googleFontLink]);

  if (isLoading) {
    return <></>;
  }

  // Determine font-family CSS based on font type
  const fontFamilyCss = fontData
    ? fontData.format === "system"
      ? fontData.url
      : `"${fontData.name}", sans-serif`
    : "inherit";

  // Determine if we need @font-face (only for uploaded fonts)
  const needsFontFace =
    fontData && fontData.format !== "system" && fontData.format !== "google";

  // Generate unique class for this widget instance
  const uniqueClass = `trending-card-${widgetId || "default"}`;

  // Generate @font-face CSS and font-family rule
  const styleCss = `
    ${
      needsFontFace
        ? `
      @font-face {
        font-family: "${fontData.name}";
        src: url("${fontData.url}") format("${fontData.format}");
        font-display: swap;
      }
    `
        : ""
    }
    ${
      fontFamilyCss !== "inherit"
        ? `.${uniqueClass} [data-title-text] { font-family: ${fontFamilyCss} !important; }`
        : ""
    }
  `.trim();

  // Create enhanced state with resolved fontFamily CSS
  const enhancedState: TrendingCardWidgetState & {
    titleFontFamilyCss?: string;
  } = {
    ...state,
    titleFontFamilyCss: fontFamilyCss,
  };

  const widgetContent = (
    <>
      {styleCss && <style>{styleCss}</style>}
      {children(enhancedState, resolveColor)}
    </>
  );

  return (
    <WidgetWrapper
      widgetId={widgetId}
      displayName={displayName}
      editFields={editFields}
      editValues={getEditValues()}
      onEditChange={handleEditChange}
      onSave={canSave ? handleSave : undefined}
      isSaving={isSaving}
      themeColors={themeColors}
      themeFonts={themeFonts}
    >
      {widgetContent}
    </WidgetWrapper>
  );
};
